import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import {
  PawPrint,
  Plus,
  MapPin,
  Bell,
  Settings,
  ChevronRight,
  Loader2,
  AlertCircle,
  QrCode,
  Calendar,
  Phone,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import LogOut from '../components/layout/LogOut';

export function Consultas() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isRoot, logout } = useAuth();
  const [mascotas, setMascotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Verificar autenticación y root
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }

    if (isRoot) {
      navigate('/admin', { replace: true });
      return;
    }

    const userId = user?.id || localStorage.getItem('userId');
    if (id && String(id) !== String(userId)) {
      navigate(`/consultas/${userId}`, { replace: true });
      return;
    }
  }, [isAuthenticated, isRoot, id, user, navigate]);

  // Cargar mascotas del usuario
  useEffect(() => {
    if (!isAuthenticated || isRoot) return;

    const userId = user?.id || localStorage.getItem('userId');
    if (!userId) return;

    const fetchMascotas = async () => {
      try {
        setLoading(true);
        const res = await api.getUserCodes(userId);
        const data = res.data;

        if (data.success) {
          setMascotas(data.data || []);
        } else {
          setError(data.error || 'Error al cargar mascotas');
        }
      } catch (err) {
        console.error('Error cargando mascotas:', err);
        setError('No se pudieron cargar tus mascotas');
      } finally {
        setLoading(false);
      }
    };

    fetchMascotas();
  }, [isAuthenticated, isRoot, user]);

  if (loading) {
    return (
      <main className="min-h-screen bg-sp-light flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-sp-primary animate-spin mx-auto mb-3" />
          <p className="text-sp-muted font-semibold text-sm">Cargando tus mascotas...</p>
        </div>
      </main>
    );
  }

  const nombreCompleto = user
    ? `${user.nombre || ''} ${user.apellido || ''}`.trim()
    : localStorage.getItem('userName') || 'Usuario';

  return (
    <main className="min-h-screen bg-sp-light mt-5">
      {/* Header del panel */}
      <section className="bg-gradient-to-br from-sp-dark to-sp-primary pt-6 pb-12 px-4 xs:px-5 relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto mt-3">
          {/* Stats rápidas */}
          <div className="grid grid-cols-3 gap-2 xs:gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 xs:p-4 text-center">
              <PawPrint className="w-5 h-5 xs:w-6 xs:h-6 text-sp-pink mx-auto mb-1" />
              <p className="text-white font-black text-lg xs:text-xl">{mascotas.length}</p>
              <p className="text-white/70 text-[0.65rem] xs:text-[0.7rem] font-bold uppercase tracking-wide">
                Mascotas
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 xs:p-4 text-center">
              <Bell className="w-5 h-5 xs:w-6 xs:h-6 text-sp-pink mx-auto mb-1" />
              <p className="text-white font-black text-lg xs:text-xl">
                {mascotas.filter((m) => m.mascota?.mensajeRescate).length}
              </p>
              <p className="text-white/70 text-[0.65rem] xs:text-[0.7rem] font-bold uppercase tracking-wide">
                Alertas
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 xs:p-4 text-center">
              <QrCode className="w-5 h-5 xs:w-6 xs:h-6 text-sp-pink mx-auto mb-1" />
              <p className="text-white font-black text-lg xs:text-xl">
                {mascotas.filter((m) => m.codigo_unico).length}
              </p>
              <p className="text-white/70 text-[0.65rem] xs:text-[0.7rem] font-bold uppercase tracking-wide">
                Códigos
              </p>
            </div>
          </div>
        </div>

        {/* Curva inferior */}
        <div
          className="absolute -bottom-0.5 left-0 right-0 h-8 bg-sp-light"
          style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }}
        />
      </section>

      {/* Contenido principal */}
      <section className="max-w-[1100px] mx-auto px-4 xs:px-5 py-6 xs:py-8">
        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 text-red-600 text-[0.85rem] font-semibold px-4 py-3 rounded-xl mb-5 flex items-center gap-2"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </motion.div>
        )}

        {/* Header de sección */}
        <div className="flex items-center justify-between mb-5 xs:mb-6">
          <div>
            <h2 className="text-sp-text text-[1.1rem] xs:text-[1.3rem] font-black">
              Mis mascotas
            </h2>
            <p className="text-sp-muted text-[0.8rem] xs:text-[0.85rem] font-semibold">
              Gestiona tus chapitas SmartPet
            </p>
          </div>
          <button
            onClick={() => {
              const codigo = prompt('Ingresá el código de 8 caracteres de tu chapita:');
              if (codigo) vincularCodigo(codigo);
            }}
            className="h-10 xs:h-11 px-4 xs:px-5 bg-sp-cta text-white rounded-5xl font-extrabold text-[0.8rem] xs:text-[0.85rem] shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden xs:inline">Agregar</span>
          </button>
        </div>

        {/* Lista de mascotas */}
        {mascotas.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 xs:p-12 shadow-sp text-center">
            <div className="w-16 h-16 xs:w-20 xs:h-20 bg-sp-label rounded-full flex items-center justify-center mx-auto mb-4">
              <PawPrint className="w-8 h-8 xs:w-10 xs:h-10 text-sp-primary/40" />
            </div>
            <h3 className="text-sp-text font-black text-[1.1rem] xs:text-[1.2rem] mb-2">
              No tenés mascotas vinculadas
            </h3>
            <p className="text-sp-muted text-[0.85rem] xs:text-[0.9rem] font-semibold mb-5 max-w-[280px] mx-auto">
              Comprá una chapita SmartPet y vinculala con tu código único para empezar.
            </p>
            <Link
              to="/comprar"
              className="inline-block h-11 px-6 bg-sp-primary text-white rounded-5xl font-extrabold text-[0.9rem] shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 transition-all"
            >
              Ver productos
            </Link>
          </div>
        ) : (
          <div className="space-y-3 xs:space-y-4">
            <AnimatePresence>
              {mascotas.map((item, index) => {
                const mascota = item.mascota;
                const tieneDatos = mascota && mascota.nombre;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl xs:rounded-3xl p-4 xs:p-5 shadow-sp hover:shadow-sp-hover transition-all cursor-pointer active:scale-[0.99]"
                    onClick={() => {
                      navigate(`/mascota/${mascota?.id || item.codigo_id}`);
                    }}
                  >
                    <div className="flex items-center gap-3 xs:gap-4">
                      {/* Foto */}
                      <div className="w-14 h-14 xs:w-16 xs:h-16 rounded-2xl bg-sp-surface flex-shrink-0 overflow-hidden">
                        {mascota?.urlImg ? (
                          <img
                            src={
                              mascota.urlImg.startsWith('http')
                                ? mascota.urlImg
                                : `http://localhost/api-smartpet/${mascota.urlImg}`
                            }
                            alt={mascota.nombre}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <PawPrint className="w-6 h-6 xs:w-7 xs:h-7 text-sp-primary/30" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sp-text font-black text-[0.95rem] xs:text-[1.05rem] truncate">
                          {tieneDatos ? mascota.nombre : 'Mascota sin nombre'}
                        </h3>
                        <p className="text-sp-muted text-[0.75rem] xs:text-[0.8rem] font-semibold truncate">
                          {item.codigo_unico || 'Sin código'}
                        </p>
                        {mascota?.direccion && (
                          <p className="text-sp-body text-[0.7rem] xs:text-[0.75rem] font-semibold flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{mascota.direccion}</span>
                          </p>
                        )}
                      </div>

                      {/* Acciones */}
                      <div className="flex items-center gap-1 xs:gap-2">
                        {mascota?.mensajeRescate && (
                          <span className="w-2 h-2 xs:w-2.5 xs:h-2.5 bg-sp-cta rounded-full flex-shrink-0" title="Tiene alertas" />
                        )}
                        <ChevronRight className="w-5 h-5 text-sp-muted" />
                      </div>
                    </div>

                    {/* Botones de acción rápida */}
                    {tieneDatos && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-sp-light">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/consultas/${id}/agenda`);
                          }}
                          className="flex-1 h-9 xs:h-10 bg-sp-label text-sp-primary rounded-xl font-bold text-[0.75rem] xs:text-[0.8rem] flex items-center justify-center gap-1.5 hover:bg-sp-surface transition-colors"
                        >
                          <Calendar className="w-3.5 h-3.5" />
                          Agenda
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // 🔥 REDIRECCIÓN INTERNA A QR (descargable)
                            navigate(`/consultas/${id}/qr/${mascota.id}`);
                          }}
                          className="flex-1 h-9 xs:h-10 bg-sp-label text-sp-primary rounded-xl font-bold text-[0.75rem] xs:text-[0.8rem] flex items-center justify-center gap-1.5 hover:bg-sp-surface transition-colors"
                        >
                          <QrCode className="w-3.5 h-3.5" />
                          Ver QR
                        </button>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Links útiles */}
        <div className="mt-6 xs:mt-8 grid grid-cols-2 gap-3 xs:gap-4">
          <Link
            to={`/consultas/${id}/configuracion`}
            className="bg-white rounded-2xl p-4 xs:p-5 shadow-sp hover:shadow-sp-hover transition-all text-center"
          >
            <Settings className="w-6 h-6 xs:w-7 xs:h-7 text-sp-primary mx-auto mb-2" />
            <p className="text-sp-text font-bold text-[0.85rem] xs:text-[0.9rem]">Configuración</p>
            <p className="text-sp-muted text-[0.7rem] xs:text-[0.75rem] font-semibold">Editar perfil</p>
          </Link>
          <button
            onClick={() => {
              logout();
              navigate('/', { replace: true });
            }}
            className="bg-white rounded-2xl p-4 xs:p-5 shadow-sp hover:shadow-sp-hover transition-all text-center"
          >
            <LogOut className="w-6 h-6 xs:w-7 xs:h-7 text-red-400 mx-auto mb-2" />
            <p className="text-sp-text font-bold text-[0.85rem] xs:text-[0.9rem]">Cerrar sesión</p>
            <p className="text-sp-muted text-[0.7rem] xs:text-[0.75rem] font-semibold">Salir de la cuenta</p>
          </button>
        </div>
      </section>
    </main>
  );

  async function vincularCodigo(codigoUnico) {
    const userId = user?.id || localStorage.getItem('userId');
    if (!userId) return;

    try {
      setLoading(true);
      const res = await api.vincularCodigo(userId, codigoUnico.toUpperCase());
      if (res.data?.success) {
        const updated = await api.getUserCodes(userId);
        setMascotas(updated.data?.data || []);
        setError('');
      } else {
        setError(res.data?.error || 'No se pudo vincular el código');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al vincular el código');
    } finally {
      setLoading(false);
    }
  }
}